import type { RowMarker } from "../../../../src/lib/audio";
import type { AdamCommand, AdamCommandContext, AdamCommandResult } from "./Command";

export type CellPatch = {
  step: number;
  track: number;
  /** Full CELL_WIDTH glyph buffer before the edit. */
  from: string;
  /** Full CELL_WIDTH glyph buffer after the edit. */
  to: string;
};

/**
 * Sparse whole-cell patches. Typing merges when `mergeGen` matches
 * (PaintController’s typing coalesce generation).
 */
export class SetCellsCommand implements AdamCommand {
  readonly id = "setCells";

  constructor(
    readonly patches: CellPatch[],
    readonly options: { label?: string; mergeGen?: number } = {},
  ) {}

  get label(): string {
    return this.options.label ?? "Edit cells";
  }

  execute(context: AdamCommandContext): AdamCommandResult {
    for (const patch of this.patches) {
      context.setCell(patch.step, patch.track, patch.to);
    }
    return { changed: "cells" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    for (const patch of this.patches) {
      context.setCell(patch.step, patch.track, patch.from);
    }
    return { changed: "cells" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof SetCellsCommand
      && this.options.mergeGen !== undefined
      && command.options.mergeGen === this.options.mergeGen;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetCellsCommand) || !this.canMergeWith(command)) {
      return this;
    }
    const map = new Map<string, CellPatch>();
    for (const patch of this.patches) {
      map.set(cellKey(patch.step, patch.track), { ...patch });
    }
    for (const patch of command.patches) {
      const key = cellKey(patch.step, patch.track);
      const prev = map.get(key);
      if (prev) {
        map.set(key, { ...prev, to: patch.to });
      } else {
        map.set(key, { ...patch });
      }
    }
    return new SetCellsCommand([...map.values()], {
      label: this.label,
      mergeGen: this.options.mergeGen,
    });
  }
}

export class SetTrackNameCommand implements AdamCommand {
  readonly id = "setTrackName";
  readonly label = "Rename track";

  constructor(
    readonly track: number,
    readonly from: string | undefined,
    readonly to: string | undefined,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    context.setTrackName(this.track, this.to);
    return { changed: "trackName" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    context.setTrackName(this.track, this.from);
    return { changed: "trackName" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }

  canMergeWith(command: AdamCommand): boolean {
    return command instanceof SetTrackNameCommand && command.track === this.track;
  }

  merge(command: AdamCommand): AdamCommand {
    if (!(command instanceof SetTrackNameCommand) || !this.canMergeWith(command)) {
      return this;
    }
    return new SetTrackNameCommand(this.track, this.from, command.to);
  }
}

export class SetRowMarkerCommand implements AdamCommand {
  readonly id = "setRowMarker";
  readonly label = "Row marker";

  constructor(
    readonly patternId: string,
    readonly localStep: number,
    readonly from: RowMarker | undefined,
    readonly to: RowMarker | undefined,
  ) {}

  execute(context: AdamCommandContext): AdamCommandResult {
    context.setRowMarker(this.patternId, this.localStep, cloneMarker(this.to));
    return { changed: "rowMarker" };
  }

  undo(context: AdamCommandContext): AdamCommandResult {
    context.setRowMarker(this.patternId, this.localStep, cloneMarker(this.from));
    return { changed: "rowMarker" };
  }

  redo(context: AdamCommandContext): AdamCommandResult {
    return this.execute(context);
  }
}

function cellKey(step: number, track: number): string {
  return `${step}:${track}`;
}

function cloneMarker(marker: RowMarker | undefined): RowMarker | undefined {
  return marker ? { label: marker.label, color: marker.color } : undefined;
}
